/**
 * Authoritative async modifier-evaluation client.
 *
 * All production modifier evaluation routes through the modifier worker. The
 * shared synchronous implementation lives in `modifierPipeline.ts` and is
 * owned by the worker.
 */

import { collectModifierEvaluationDependencies } from "./modifiers/evaluation.ts";
import type { ModifierWorkerRequest } from "./modifiers/modifierTypes.ts";
import { evaluateModifierStackAsync as evaluateInWorker } from "./modifiers/modifierWorkerClient.ts";

export { modifierStackKey } from "./modifierPipeline.ts";

let _modifierCacheEpoch = 0;

export function clearModifierCache(): void {
  _modifierCacheEpoch++;
}

export function getModifierCacheEpoch(): number {
  return _modifierCacheEpoch;
}

export function buildModifierEvaluationRequest(
  request: Omit<
    ModifierWorkerRequest,
    | "cacheEpoch"
    | "sceneObjectsByName"
    | "sceneObjectCacheKeys"
    | "modifierOverrides"
  > & {
    sceneObjectsByName?: ReadonlyMap<
      string,
      import("../modeling/types.ts").SceneObject
    >;
    modifierOverrides?: ReadonlyMap<
      string,
      readonly import("../app/storeTypes.ts").ModifierOverride[]
    >;
  },
): Omit<ModifierWorkerRequest, "cacheEpoch"> {
  const dependencies = request.sceneObjectsByName
    ? collectModifierEvaluationDependencies(
        request.modifiers,
        request.sceneObjectsByName,
        request.modifierOverrides,
      )
    : null;
  const sceneObjectCacheKeys = new Map<string, string>();
  if (dependencies) {
    for (const [name, key] of dependencies.sceneObjectCacheKeys) {
      sceneObjectCacheKeys.set(name, key);
    }
  }
  if (request.runtimeSourceCacheKeys) {
    for (const [name, key] of request.runtimeSourceCacheKeys) {
      sceneObjectCacheKeys.set(name, key);
    }
  }

  return {
    baseCacheKey: request.baseCacheKey,
    geometry: request.geometry,
    owner: request.owner,
    modifiers: request.modifiers,
    sceneObjectsByName:
      dependencies && dependencies.sceneObjectsByName.size > 0
        ? dependencies.sceneObjectsByName
        : undefined,
    sceneObjectCacheKeys:
      sceneObjectCacheKeys.size > 0 ? sceneObjectCacheKeys : undefined,
    runtimeSourceGeometries: request.runtimeSourceGeometries,
    runtimeSourceCacheKeys: request.runtimeSourceCacheKeys,
    modifierOverrides:
      dependencies && dependencies.modifierOverrides.size > 0
        ? dependencies.modifierOverrides
        : undefined,
  };
}

export async function evaluateModifierStackAsync(
  request: Omit<ModifierWorkerRequest, "cacheEpoch">,
) {
  return evaluateInWorker({
    ...request,
    cacheEpoch: _modifierCacheEpoch,
  });
}
